<template>
  <section class="pricing-section" id="pricing" aria-label="Pricing section">
    <div class="background-elements">
      <div class="grid-overlay"></div>
    </div>

    <div class="content-container">
      <div class="section-header">
        <div class="brand-tag slide-in" data-delay="100">
          <span>Pricing Plans</span>
        </div>

        <h2 class="main-heading">
          <div class="heading-line fade-in" data-delay="300">Choose Your</div>
          <div class="heading-highlight fade-in" data-delay="500">
            Perfect Plan
            <svg
              class="heading-accent"
              viewBox="0 0 100 100"
              width="80"
              height="80"
            >
              <circle cx="50" cy="50" r="40" class="accent-circle" />
            </svg>
          </div>
        </h2>
      </div>

      <div class="pricing-grid fade-in" data-delay="700">
        <div
          v-for="plan in plans"
          :key="plan.title"
          class="pricing-card"
          :class="{ popular: plan.popular }"
        >
          <div class="card-content">
            <span class="plan-type">{{ plan.badge }}</span>
            <h3 class="plan-title">{{ plan.title }}</h3>
            <div class="price">
              <span class="amount">{{ plan.price }}</span>
              <span class="term">{{ plan.term }}</span>
            </div>
            <ul class="features">
              <li v-for="feature in plan.features" :key="feature">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
                {{ feature }}
              </li>
            </ul>
            <a href="#contact" class="cta-button">Get Started</a>
          </div>
        </div>
      </div>

      <div class="all-services-note fade-in" data-delay="900">
        <h3>Any Web Development Project</h3>
        <p>
          We handle all types of web development projects. From simple websites
          to complex web applications - if it's web-based, we can build it.
        </p>
        <div class="capabilities">
          <span class="capability-tag">Custom Web Apps</span>
          <span class="capability-tag">API Development</span>
          <span class="capability-tag">CMS Solutions</span>
          <span class="capability-tag">SaaS Platforms</span>
          <span class="capability-tag">Progressive Web Apps</span>
          <span class="capability-tag">Dashboard Systems</span>
          <span class="capability-tag">Booking Systems</span>
          <span class="capability-tag">Real-time Applications</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref } from "vue";

export default {
  name: "PricingSection",
  setup() {
    const plans = ref([
      {
        title: "Landing Page",
        badge: "Basic",
        price: "$1,500",
        term: "10-20 days",
        features: [
          "1 page design",
          "Up to 6 sections",
          "Mobile responsive",
          "Basic SEO setup",
          "Contact form integration",
        ],
      },
      {
        title: "Business Website",
        badge: "Pro",
        price: "$2,500-3,000",
        term: "20-30 days",
        features: [
          "Up to 6 pages",
          "Custom design",
          "SEO optimization",
          "Speed optimization",
          "Content management system",
        ],
        popular: true,
      },
      {
        title: "Corporate Website",
        badge: "Enterprise",
        price: "$5,000",
        term: "25-30 days",
        features: [
          "Multi-page website",
          "Advanced features",
          "Premium support",
          "Custom functionality",
          "Advanced SEO",
        ],
      },
      {
        title: "E-Commerce Shop",
        badge: "Business",
        price: "Individual",
        term: "Consultation required",
        features: [
          "Online store setup",
          "Product catalog",
          "Payment integration",
          "Order management",
          "Admin dashboard",
        ],
      },
      {
        title: "Custom Systems",
        badge: "Custom",
        price: "Individual",
        term: "Based on scope",
        features: [
          "Tailored solutions",
          "CRM systems",
          "Management platforms",
          "API integrations",
          "Database architecture",
        ],
      },
      {
        title: "Telegram Bots",
        badge: "Automation",
        price: "Individual",
        term: "10-20 days",
        features: [
          "Custom bot development",
          "Payment processing",
          "Admin panels",
          "Database integration",
          "Automated workflows",
        ],
      },
    ]);

    return {
      plans,
    };
  },
};
</script>

<style lang="scss" scoped>
.pricing-section {
  --primary-color: #6366f1;
  --accent-color: #818cf8;
  --text-primary: #ffffff;
  --text-secondary: #e2e8f0;

  background-color: #000000;
  position: relative;
  padding: 6rem 0;
  overflow: hidden;
}

.background-elements {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.gradient-sphere {
  position: absolute;
  top: -20%;
  right: -10%;
  width: 80vh;
  height: 80vh;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.2) 0%,
    rgba(99, 102, 241, 0.1) 40%,
    transparent 70%
  );
  border-radius: 50%;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(#3d73ff08 1px, transparent 1px),
    linear-gradient(90deg, #3d73ff08 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
}

.content-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.brand-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 100px;
  margin-bottom: 1.5rem;

  .tag-dot {
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: 0.05em;
  }
}

.main-heading {
  .heading-line {
    font-size: 1.5rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .heading-highlight {
    position: relative;
    font-size: 3rem;
    font-weight: 700;
    color: var(--text-primary);

    .heading-accent {
      position: absolute;
      top: -20px;
      right: -40px;
      z-index: -1;

      .accent-circle {
        fill: none;
        stroke: var(--accent-color);
        stroke-width: 2;
        stroke-dasharray: 251;
        stroke-dashoffset: 251;
        animation: drawCircle 2s ease forwards;
      }
    }
  }
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;
}

.pricing-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 2.5rem;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-8px);
  }

  &.popular {
    background: rgba(99, 102, 241, 0.05);
    border-color: rgba(99, 102, 241, 0.2);
  }
}

.plan-type {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 100px;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;
}

.plan-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.5rem;
}

.price {
  margin-bottom: 2rem;

  .amount {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--primary-color);
  }

  .term {
    display: block;
    color: var(--text-secondary);
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }
}

.features {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 1rem;
    font-size: 0.9375rem;

    svg {
      fill: var(--accent-color);
      flex-shrink: 0;
    }
  }
}

.cta-button {
  display: inline-block;
  width: 100%;
  padding: 0.875rem;
  background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
  color: var(--text-primary);
  text-align: center;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.9;
  }
}

.all-services-note {
  text-align: center;
  margin-top: 4rem;
  padding: 2rem;
  background: rgba(99, 102, 241, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;

  h3 {
    color: var(--text-primary);
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  p {
    color: var(--text-secondary);
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .capabilities {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    margin-top: 1.5rem;

    .capability-tag {
      padding: 0.75rem 1.5rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 100px;
      color: var(--text-secondary);
      font-size: 0.9375rem;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(99, 102, 241, 0.1);
        border-color: rgba(99, 102, 241, 0.3);
        color: var(--text-primary);
      }
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-in {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 1024px) {
  .pricing-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .all-services-note .capabilities {
    gap: 0.75rem;

    .capability-tag {
      font-size: 0.875rem;
      padding: 0.625rem 1.25rem;
    }
  }
}

@media (max-width: 768px) {
  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .content-container {
    padding: 0 1.5rem;
  }

  .main-heading .heading-highlight {
    font-size: 2.5rem;
  }

  .all-services-note {
    padding: 1.5rem;

    h3 {
      font-size: 1.25rem;
    }

    p {
      font-size: 0.9375rem;
    }

    .capabilities {
      .capability-tag {
        font-size: 0.8125rem;
        padding: 0.5rem 1rem;
      }
    }
  }
}
</style>
